import React from "react";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import ReactDOM from "react-dom/client";
import "./variables.css";
import "./index.css";
import "./tailwindstyles.css";
import App from "./App";
import "./i18n/i18n";
import { ThemeProvider } from "./store/ThemeContext";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

if (process.env.REACT_APP_ENABLE_WASM_QR_SCANNER === "true") {
  import("./utils/zxingWasm");
}

if (process.env.REACT_APP_OTEL_ENABLED === "true") {
  import("./telemetry");
}

// avoid reporting "no camera device" as an unhandled rejection.
window.addEventListener("unhandledrejection", (event) => {
  const reason = event?.reason;
  const name =
    (reason && typeof reason === "object" && "name" in reason && reason.name) ||
    "";
  const message =
    (reason &&
      typeof reason === "object" &&
      "message" in reason &&
      reason.message) ||
    "";

  const isCameraNotFound =
    /requested device not found|camera|videoinput|getusermedia|media(stream)?|track/i.test(
      String(message || ""),
    );

  if (name === "NotFoundError" && isCameraNotFound) {
    event.preventDefault();
  }

  // "Permission dismissed" means the user closed the camera prompt without choosing.
  // Only suppress when our scanner was actively requesting camera access, so we don't
  // accidentally hide NotAllowedError from unrelated permission flows (clipboard, etc.).
  if (
    name === "NotAllowedError" &&
    window.__cameraPermissionPending &&
    /permission dismissed/i.test(String(message || ""))
  ) {
    window.__cameraPermissionPending = false;
    event.preventDefault();
  }
});

// PostHog initialization
const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const posthogHost =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

if (posthogKey && process.env.NODE_ENV !== "test") {
  posthog.init(posthogKey, {
    api_host: posthogHost,
  });
}

// Sentry initialization — production builds only (skips localhost / npm start)
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

if (sentryDsn && process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    tracesSampleRate: 0.2,
    sendDefaultPii: false,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
    ],
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.Suspense fallback="...loading">
    <BrowserRouter>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </React.Suspense>,
);
